import { APITypes, Dynamo, LambdaInvocationTypes } from '..';
import {
    ContactDetails as DynamoContactDetails,
    InsuranceCareTeamMetadata as DynamoInsuranceCareTeamMetadata,
    SupportCareTeamMetadata as DynamoSupportCareTeamMetadata,
    ProviderCareTeamMetadata as DynamoProviderCareTeamMetadata,
    CareTeamMemberTypes as DynamoCareTeamMemberTypes,
    User as DynamoUser,
    MedicalProfileIdCard as DynamoMedicalProfileIdCard,
    MedicalProfileInsuranceCard as DynamoMedicalProfileInsuranceCard,
    CodeStatus,
} from '../DynamoTypes';
import { InAppNotification, ReadOrUnreadInAppNotification, UnreadInAppNotification } from '../NotificationTypes';
import { AppPermissions } from '../abac';
import { MaskedOrganizationDetails, OrganizationInvoiceDetails } from './organizationService';
import { AvatarChange, EntityReferences, GetAvatarResponse } from './sharedTypes';

export type MedicalProfileContactDetails = Pick<
    DynamoContactDetails,
    | 'contactDetailsId'
    | 'sourceEntityId'
    | 'firstName'
    | 'lastName'
    | 'preferredFirstName'
    | 'dob'
    | 'sex'
    | 'gender'
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'zipCode'
    | 'email'
    | 'phoneNumber'
> & {
    entityType: 'medical-profile';
    contactType: 'entity-contact';
};

export type EmergencyContactDetails = Pick<
    DynamoContactDetails,
    'contactDetailsId' | 'sourceEntityId' | 'firstName' | 'lastName' | 'relationship' | 'email' | 'phoneNumber'
> & {
    entityType: 'medical-profile';
    contactType: 'emergency-contact';
};

export type SupportCareTeamContactDetails = Pick<
    DynamoContactDetails,
    | 'firstName'
    | 'lastName'
    | 'company'
    | 'description'
    | 'phoneNumber'
    | 'faxNumber'
    | 'email'
    | 'url'
    | 'role'
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'zipCode'
> & {
    entityType: 'care-team-member';
    contactType: 'support-care-team-member';
};

export type ProviderCareTeamContactDetails = Pick<
    DynamoContactDetails,
    | 'firstName'
    | 'lastName'
    | 'company'
    | 'description'
    | 'phoneNumber'
    | 'faxNumber'
    | 'email'
    | 'url'
    | 'role'
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'zipCode'
> & {
    entityType: 'care-team-member';
    contactType: 'provider-care-team-member';
};

export type InsuranceCareTeamContactDetails = Pick<
    DynamoContactDetails,
    | 'description'
    | 'phoneNumber'
    | 'faxNumber'
    | 'email'
    | 'url'
    | 'role'
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'zipCode'
> & {
    entityType: 'care-team-member';
    contactType: 'insurance-care-team-member';
};

export type ContactDetailsUpsert<TContactDetails> = Omit<TContactDetails, 'contactDetailsId' | 'sourceEntityId'>;
export type MedicalProfilesContactDetailsUpsert = ContactDetailsUpsert<MedicalProfileContactDetails>;
export type EmergencyContactDetailsUpsert = ContactDetailsUpsert<EmergencyContactDetails>;
export type SupportCareTeamContactDetailsUpsert = ContactDetailsUpsert<SupportCareTeamContactDetails>;
export type ProviderCareTeamContactDetailsUpsert = ContactDetailsUpsert<ProviderCareTeamContactDetails>;
export type InsuranceCareTeamContactDetailsUpsert = ContactDetailsUpsert<InsuranceCareTeamContactDetails>;

export type CreateMedicalProfileRequest = {
    contactDetails: MedicalProfilesContactDetailsUpsert;
    emergencyContactDetails: EmergencyContactDetailsUpsert[];
    avatar: AvatarChange;
    isUserMedicalProfile?: boolean;
};

type UpsertProfileCardRequest = {
    description: string;
    image: AvatarChange;
};

export type UpsertMedicalProfileIdCardRequest = UpsertProfileCardRequest;
export type UpsertMedicalProfileInsuranceCardRequest = UpsertProfileCardRequest;

export type WithTimestamp<T> = T & { createdAt: string | null };

export type GetMedicalProfilesIdCardsResponse = {
    idCards: WithTimestamp<DynamoMedicalProfileIdCard>[];
    insuranceCards: WithTimestamp<DynamoMedicalProfileInsuranceCard>[];
};

export type GetIdCardResponse = GetAvatarResponse | null;

export type CreateMedicalProfileResponse = {
    medicalProfileId: string;
};

export type ProfileListItem = {
    medicalProfileId: string;
} & MedicalProfileContactDetails;

export type ListMedicalProfilesResponse = {
    profiles: ProfileListItem[];
    collaboratorProfiles: ProfileListItem[];
};

export type GetProfileAvatarUrlResponse = GetAvatarResponse | null;

export type BaseCareTeamProps = {
    careTeamMemberId: string;
};

export type CareTeamMemberTypes = DynamoCareTeamMemberTypes;

export type SupportCareTeamMetadata = DynamoSupportCareTeamMetadata;

export type SupportCareTeamMemberDetails<
    TContactDetails extends SupportCareTeamContactDetails | SupportCareTeamContactDetailsUpsert,
> = {
    careTeamMemberType: 'support';
    contactDetails: TContactDetails;
    metadata: SupportCareTeamMetadata;
} & BaseCareTeamProps;

export type ProviderCareTeamMetadata = DynamoProviderCareTeamMetadata;

export type ProviderCareTeamMemberDetails<
    TContactDetails extends ProviderCareTeamContactDetails | ProviderCareTeamContactDetailsUpsert,
> = {
    careTeamMemberType: 'provider';
    contactDetails: TContactDetails;
    metadata: ProviderCareTeamMetadata;
} & BaseCareTeamProps;

export type InsuranceCareTeamMetadata = DynamoInsuranceCareTeamMetadata;

export type InsuranceCareTeamMemberDetails<
    TContactDetails extends InsuranceCareTeamContactDetails | InsuranceCareTeamContactDetailsUpsert,
> = {
    careTeamMemberType: 'insurance';
    contactDetails: TContactDetails;
    metadata: InsuranceCareTeamMetadata;
    insuranceCardFront: AvatarChange;
    insuranceCardBack: AvatarChange;
} & BaseCareTeamProps;

export type CareTeamMemberDetails = (
    | SupportCareTeamMemberDetails<SupportCareTeamContactDetails>
    | ProviderCareTeamMemberDetails<ProviderCareTeamContactDetails>
    | InsuranceCareTeamMemberDetails<InsuranceCareTeamContactDetails>
) & {
    references: EntityReferences;
};

export type ListResourceGroupRelatedCareTeamMembersRequest = {
    groupResourceIds: string[];
};

export type ListCareTeamResponse = {
    careTeam: CareTeamMemberDetails[];
};

export type UpsertCareTeamMember = (
    | Omit<SupportCareTeamMemberDetails<SupportCareTeamContactDetailsUpsert>, 'careTeamMemberId'>
    | Omit<ProviderCareTeamMemberDetails<ProviderCareTeamContactDetailsUpsert>, 'careTeamMemberId'>
    | Omit<InsuranceCareTeamMemberDetails<InsuranceCareTeamContactDetailsUpsert>, 'careTeamMemberId'>
) & {
    avatar: AvatarChange;
};

export type CreateCareTeamMemberRequest = UpsertCareTeamMember & {
    associatedResourceId: string | null;
};

export type CreateCareTeamMemberResponse = {
    careTeamMemberId: string;
};

export type GetCareTeamAvatarUrlResponse = GetAvatarResponse | null;

export type GetCareTeamMemberResponse = CareTeamMemberDetails;

export type UpdateCareTeamMemberRequest = UpsertCareTeamMember;

export type GetProfileResponse = {
    medicalProfileId: string;
    /**
     * Whether or not the profile is provisioned by an organization
     *
     * This will only return true if the profile is provisioned and
     * if the *calling* user belongs to that provisioning organization.
     */
    isProvisionedMedicalProfile?: boolean;
    isFastenEnabled?: boolean;
    noKnownDrugAllergies?: boolean;
    contactDetails: MedicalProfileContactDetails;
    emergencyContactDetails: EmergencyContactDetails[];
};

export type UpdateMedicalProfileContactsDetails = {
    contactDetails: MedicalProfilesContactDetailsUpsert;
    avatar: AvatarChange;
};

export type UpdateEmergencyContactDetails = { contactDetails: EmergencyContactDetailsUpsert };

export type UpdateContactDetailsBody = UpdateMedicalProfileContactsDetails | UpdateEmergencyContactDetails;

export type CreateEmergencyContactDetailsBody = EmergencyContactDetailsUpsert;

export type AddCareTeamMemberRelationshipRequest = {
    targetCareTeamMemberId: string;
};

export type ListRelatedCareTeamMembersResponse = {
    relatedCareTeamMembers: CareTeamMemberDetails[];
};

/**
 * Restrict user view of a collaborator to only the fields they need to see
 *
 * We don't need to "leak" address information to a collaborator, for example.
 */
export type CollaboratorUser = Pick<DynamoUser, 'userId' | 'firstName' | 'lastName'>;

export type CollaboratorOrganization = MaskedOrganizationDetails;

export type MedicalProfileUserCollaborator = {
    type: 'user-collaborator';
    medicalProfileCollaboratorId: string;
    user: CollaboratorUser;
    appPermissions: AppPermissions | null;
    associatedCareTeamMember: CareTeamMemberDetails | null;
    lastProfileViewedDateTime: string | null;
    userEmail: string;
};

export type MedicalProfileOrganizationCollaborator = {
    type: 'organization-collaborator';
    medicalProfileCollaboratorId: string;
    organization: CollaboratorOrganization;
    appPermissions: AppPermissions | null;
    associatedCareTeamMember: CareTeamMemberDetails | null;
    lastProfileViewedDateTime: string | null;
};

export type MedicalProfileCollaborator = MedicalProfileUserCollaborator | MedicalProfileOrganizationCollaborator;

export type ListMedicalProfileCollaboratorsResponse = {
    collaborators: MedicalProfileCollaborator[];
};

export type GetMedicalProfileCollaboratorResponse = {
    collaborator: MedicalProfileCollaborator;
};

export type ListOrganizationMedicalProfileCollaboratorInvoicesResponse = {
    items: OrganizationInvoiceDetails[];
};

export type GetOrganizationMedicalProfileCollaboratorInvoiceResponse = {
    invoice: OrganizationInvoiceDetails;
};

export type CareTeamCollaborator = MedicalProfileCollaborator & {
    careTeamMemberId: string;
};

export type ListCareTeamCollaboratorsResponse = {
    collaborators: CareTeamCollaborator[];
};

export type MedicalProfileCollaboratorInviteBody = {
    email: string;
    appPermissions: AppPermissions;
    careTeamMemberId?: string;
    firstName: string;
    lastName: string;
    message: string | null;
};

export type InviteAlreadyExistsResponse = {
    status: 'invite-exists';
};

export type AlreadyCollaboratorResponse = {
    status: 'collaborator-exists';
};

export type InviteSuccessResponse = {
    status: 'success';
};

export type InviteResponse = InviteAlreadyExistsResponse | InviteSuccessResponse | AlreadyCollaboratorResponse;

export type CollaboratorInviteListItem = {
    email: string;
    medicalProfileId: string;
    associatedCareTeamMember: CareTeamMemberDetails | null;
    medicalProfile: ProfileListItem;
    appPermissions: AppPermissions | null;
    createDate: string;
    medicalProfileCollaboratorInviteId: string;
    invitedByEmail: string;
    message: string | null;
    invitedByFirstName: string | null;
    invitedByLastName: string | null;
    inviteeFirstName: string;
    inviteeLastName: string;
};

export type ListCollaboratorInvitesResponse = {
    invites: CollaboratorInviteListItem[];
};

export type CollaboratorInviteResponse = {
    inviteResponse: 'accept' | 'reject';
};

export type EvaluatedProfilePermissionsResponse = {
    isProfileOwner: boolean;
    permissions: AppPermissions;
};

export type UpdatePermissionsRequest = {
    appPermissions: AppPermissions;
};

export type InboundTransferRequest = {
    profile: ProfileListItem;
    fromUser: DynamoUser;
    medicalProfileOwnershipTransferId: string;
};

export type InboundTransferRequestsResponse = {
    inboundTransferRequests: InboundTransferRequest[];
};

export type PendingTransferRequestResponse = {
    profile: ProfileListItem;
    fromUser: DynamoUser;
    medicalProfileOwnershipTransferId: string;
    previousOwnerWantsToMaintainAccess: boolean;
    message: string | null;
};

export type PendingCollaboratorTransferRequest = {
    status: 'pending-collaborator-transfer-request';
    transferToUser: DynamoUser;
    transferFromUser: DynamoUser;
    medicalProfileOwnershipTransferId: string;
    previousOwnerWantsToMaintainAccess: boolean;
};

export type PendingNonCollaboratorTransferRequest = {
    status: 'pending-non-collaborator-transfer-request';
    transferToEmail: string;
    transferFromUser: DynamoUser;
    medicalProfileOwnershipTransferId: string;
    previousOwnerWantsToMaintainAccess: boolean;
};

export type NoPendingTransferRequest = {
    status: 'no-pending-transfer-request';
};

export type ProfileOutboundTransferResponse =
    | PendingCollaboratorTransferRequest
    | PendingNonCollaboratorTransferRequest
    | NoPendingTransferRequest;

export type CreateEmailBasedTransferRequestRequest = {
    email: string;
    maintainAccess: boolean;
    message: string | null;
};

export type CreateCollaboratorTransferRequestRequest = {
    collaboratorId: string;
    maintainAccess: boolean;
    message: string | null;
};

export type CreateTransferRequestRequest =
    | CreateEmailBasedTransferRequestRequest
    | CreateCollaboratorTransferRequestRequest;

export type TransferRequestDecisionRequest = {
    decision: 'accept' | 'reject';
    previousOwnerMaintainsAccess: boolean;
};

export type UpdateCareTeamResources = {
    relatedResourceIds: string[];
};

export type GetMedicalProfileOwnerResponse = {
    userId: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
};

export type CopyCareTeamMemberRequest = {
    destinationMedicalProfileId: string;
};

export type CopyCareTeamMemberResponse = {
    careTeamMemberId: string;
};

export type GetMedicalProfilePaymentStatusResponse = {
    isPaid: boolean;
};

export type GetCanAddMedicalProfileResponse = {
    canAddMedicalProfile: boolean;
};

export type CreateMedicalProfileDirectiveFile = {
    type: 'create';
    name: string;
    contentType: string;
    tempFileId: string;
};

export type DeleteMedicalProfileDirectiveFile = {
    type: 'delete';
    fileId: string;
};

export type UpdateMedicalProfileDirectiveFile = {
    type: 'update';
    fileId: string;
    name: string;
    contentType: string;
    tempFileId: string;
};

export type NotModifiedMedicalProfileDirectiveFile = {
    type: 'not-modified';
    fileId: string;
    name: string;
};

export type ChangeMedicalProfileDirectiveFile =
    | CreateMedicalProfileDirectiveFile
    | DeleteMedicalProfileDirectiveFile
    | UpdateMedicalProfileDirectiveFile
    | NotModifiedMedicalProfileDirectiveFile;

export type UpsertProfileCodeStatusRequest = {
    codeStatus: CodeStatus;
    codeStatusNotes: string | null;
    codeStatusFiles: ChangeMedicalProfileDirectiveFile[];
};

export type UpsertProfilePOARequest = {
    poaHealthcareProxyName: string;
    poaRelationship?: Dynamo.POARelationship;
    poaPhoneNumber?: string;
    poaFiles: ChangeMedicalProfileDirectiveFile[];
};

export type ProfileCodeStatusResponse = {
    codeStatus: CodeStatus;
    codeStatusNotes: string | null;
    codeStatusFiles: Dynamo.MedicalProfileDirectiveFile[];
    medicalProfileFiles: APITypes.medicalProfileFilesService.MedicalProfileFileListItem[];
};

export type GetProfileCodeStatusResponse = ProfileCodeStatusResponse | null;

export type ProfilePOAResponse = {
    poaHealthcareProxyName: string;
    poaRelationship: Dynamo.POARelationship;
    poaPhoneNumber?: string;
    poaFiles: Dynamo.MedicalProfileDirectiveFile[];
    medicalProfileFiles: APITypes.medicalProfileFilesService.MedicalProfileFileListItem[];
};

export type GetProfilePOAResponse = ProfilePOAResponse | null;

export type ProfileUnreadInAppNotifications =
    LambdaInvocationTypes.GetUserUnreadInAppNotificationsForProfileResponse['unreadInAppNotifications'];

export type ProfileReadInAppNotifications = InAppNotification[];

export type GetProfileUnreadInAppNotificationsResponse = {
    profileUnreadInAppNotifications: UnreadInAppNotification[];
};

export type GetAllProfileInAppNotificationsResponse = {
    profileInAppNotifications: ReadOrUnreadInAppNotification[];
};

export type ProfileUnreadInAppNotificationsCount = {
    medicalProfileId: string;
    count: number;
};

export type GetProfilesUnreadInAppNotificationsCountResponse = {
    profilesInAppNotificationsCount: ProfileUnreadInAppNotificationsCount[];
};

export type ToggleInAppNotificationsReadStatusRequest = {
    notificationsToToggle: ReadOrUnreadInAppNotification[];
};

export type AllInAppNotificationsResponse = {
    allInAppNotifications: ReadOrUnreadInAppNotification[];
};
